import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { adminApi, type Company } from "../../api/admin";
import {
  approvalWaitsForAnotherSigner,
  nextAllowedFor,
  ordersApi,
  type Order,
  type OrderLine,
  type OrderStatus,
} from "../../api/orders";
import type { ApiError } from "../../api/client";
import { stockStatus, outOfStockSkus } from "../../lib/stockStatus";
import { useToast } from "../../components/Toast";
import { OrderStatusBadge } from "../../components/OrderStatusBadge";
import { useAuth } from "../../auth/AuthContext";
import { useSectionAccess } from "../../auth/useSectionAccess";
import Ic from "../../components/icons.jsx";
import { ImportOrderModal } from "../orders/ImportOrderModal";
import { Select } from "../../components/Select";
import { ErpReadOnlyNote } from "../../components/ErpReadOnlyNote";
import { formatMoney, formatUnitPrice } from "../../lib/format";

const STATUSES: OrderStatus[] = [
  "DRAFT", "SUBMITTED", "IN_REVIEW", "APPROVED", "REJECTED",
  "SHIPPED", "INVOICED", "CANCELLED",
];

const money = (n: number | null | undefined, cur: string | null) =>
  formatMoney(n, cur);

export default function AdminOrdersPage() {
  const { i18n, t } = useTranslation();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [statusFilter, setStatusFilter] = useState<OrderStatus | "">("SUBMITTED");
  const [companyFilter, setCompanyFilter] = useState<string>("");
  const [openOrder, setOpenOrder] = useState<Order | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  async function load() {
    try {
      const [list, comps] = await Promise.all([
        ordersApi.list({
          status: statusFilter || undefined,
          companyId: companyFilter || undefined,
          size: 200,
        }),
        adminApi.listCompanies(0, 200).then((p) => p.content.filter(Boolean)),
      ]);
      setOrders(list.content);
      setCompanies(comps);
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }
  useEffect(() => { void load(); }, [statusFilter, companyFilter]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-ink)" }}>{t("admin.orders.title", "Orders queue")}</h1>
        {hasPermission("orders:import") && (
          <button type="button" className="btn" onClick={() => setImportOpen(true)}>
            <Ic.upload /> {t("orders.import.button")}
          </button>
        )}
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Select<string>
          value={companyFilter}
          onChange={setCompanyFilter}
          style={{ width: "100%" }}
          options={[
            { value: "", label: t("admin.orders.filter.allCompanies", "All companies") },
            ...companies.map((c) => ({ value: c.id, label: c.name })),
          ]}
        />
        <Select<OrderStatus | "">
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: "100%" }}
          options={[
            { value: "", label: t("orders.list.allStatuses", "All statuses") },
            ...STATUSES.map((s) => ({ value: s, label: t(`orders.status.${statusKey(s)}`, s) })),
          ]}
        />
      </div>

      {!orders ? (
        <p style={{ color: "var(--ink-3)" }}>{t("common.loading", "Loading…")}</p>
      ) : orders.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("orders.empty.title", "No orders match.")}</p>
      ) : (
        <table style={tbl}>
          <thead>
            <tr>
              <th style={th}>{t("admin.orders.col.number", "Number")}</th>
              <th style={th}>{t("admin.orders.col.company", "Company")}</th>
              <th style={th}>{t("admin.orders.col.status", "Status")}</th>
              <th style={th}>{t("admin.orders.col.lines", "Lines")}</th>
              <th style={th}>{t("admin.orders.col.submitted", "Submitted")}</th>
              <th style={th}>{t("admin.orders.col.requestedDelivery", "Requested delivery")}</th>
              <th style={{ ...th, textAlign: "right" }}>{t("common.actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id}>
                <td style={td}><code>{o.orderNumber}</code></td>
                <td style={td}>{o.companyName}</td>
                <td style={td}><OrderStatusBadge status={o.status} /></td>
                <td style={td}>{o.lines.length}</td>
                <td style={td}>{o.submittedAt ? new Date(o.submittedAt).toLocaleString(i18n.resolvedLanguage) : "—"}</td>
                <td style={td}>{o.requestedDeliveryDate ?? "—"}</td>
                <td style={td}>
                  <button type="button" onClick={() => setOpenOrder(o)} style={linkBtn}>{t("orders.list.open", "Open")}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {openOrder && (
        <OrderDetailModal
          order={openOrder}
          onClose={() => setOpenOrder(null)}
          onChanged={async () => { await load(); }}
        />
      )}

      {importOpen && <ImportOrderModal onClose={() => setImportOpen(false)} />}
    </div>
  );
}

function OrderDetailModal({
  order, onClose, onChanged,
}: {
  order: Order;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { state, hasPermission } = useAuth();
  const { canWrite: canWriteSection, canFull } = useSectionAccess("orders");
  // An order from the ERP is shown here and changed only there: no notes, no discounts, no moves.
  const fromErp = !!order.fromErp;
  const canWrite = canWriteSection && !fromErp;
  // What the server asks for, read off the endpoints this modal calls. The route only
  // guards section:orders:read, so everything below it is reachable by a reader:
  //   POST /orders/{id}/transition  -> orders:transition + section:orders:write
  //   PUT  /orders/{id}/pricing     -> orders:transition + section:orders:write
  //   PUT  /orders/{id}             -> section:orders:write (sales notes, internal caller)
  // Same pair OrderEditorPage gates its sales review on; the two screens do the same
  // work and must not drift apart.
  const canReview = canWrite && hasPermission("orders:transition");
  // Identity of the signed-in user: the approval is withheld from whoever
  // entered the order, so the lifecycle buttons depend on who is looking.
  const viewerId = state.status === "authenticated" ? state.user.id : null;
  const [current, setCurrent] = useState<Order>(order);
  const [salesNotes, setSalesNotes] = useState(order.salesNotes ?? "");
  const [busy, setBusy] = useState(false);

  const inReview = current.status === "IN_REVIEW";
  // Pricing is a write: the status alone only decides whether the reason column is worth
  // showing. A reader keeps the numbers and the reasons, just not the inputs and the
  // button the server would refuse.
  const canPrice = inReview && canReview;
  // Stock check: soft, placeholder-backed warning in the pre-approval
  // window. Never blocks a transition — purely informational. lib/stockStatus
  // is the single ERP swap point.
  const showStockWarning = current.status === "SUBMITTED" || current.status === "IN_REVIEW";
  const outOfStockSkuList = showStockWarning ? outOfStockSkus(current.lines.map((l) => l.productSku)) : [];
  const [edits, setEdits] = useState<Record<string, { final: string; reason: string }>>(() =>
    Object.fromEntries(current.lines.map((l) => [l.id, {
      final: String(l.finalDiscountPct ?? l.contractedDiscountPct ?? 0),
      reason: l.discountReason ?? "",
    }])),
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkPct, setBulkPct] = useState("");
  const [bulkReason, setBulkReason] = useState("");

  const setEdit = (id: string, patch: Partial<{ final: string; reason: string }>) =>
    setEdits((m) => ({ ...m, [id]: { ...m[id], ...patch } }));
  const toggleSel = (id: string) =>
    setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const applyBulk = () => {
    if (selected.size === 0) { toast.error(t("admin.orders.bulk.selectFirst", "Select lines first.")); return; }
    setEdits((m) => {
      const n = { ...m };
      selected.forEach((id) => { n[id] = { final: bulkPct, reason: bulkReason }; });
      return n;
    });
  };

  const liveFinal = (l: OrderLine) => {
    const v = Number(edits[l.id]?.final);
    return Number.isFinite(v) ? v : l.finalDiscountPct;
  };
  // Same rule as the server: the net price keeps four decimals, and the line total is taken from the
  // unrounded net and rounded to two, so 2,000 × 0.505 stays 1,010.00.
  const exactNet = (l: OrderLine) =>
    l.catalogUnitPrice == null ? null : l.catalogUnitPrice * (1 - liveFinal(l) / 100);
  const liveNet = (l: OrderLine) => {
    const n = exactNet(l);
    return n == null ? null : Math.round(n * 10000) / 10000;
  };
  const liveTotal = (l: OrderLine) => {
    const n = exactNet(l);
    return n == null || l.quantity == null ? null : Math.round(n * l.quantity * 100) / 100;
  };
  const liveOrderTotal = current.lines.reduce((s, l) => s + (liveTotal(l) ?? 0), 0);

  async function savePricing() {
    if (!canReview) return;
    const payload: { lineId: string; finalDiscountPct: number; reason?: string | null }[] = [];
    for (const l of current.lines) {
      if (!l.productId) continue;
      const e = edits[l.id];
      if (!e) continue;
      const f = Number(e.final);
      if (!Number.isFinite(f) || f < 0 || f > 100) { toast.error(t("admin.orders.error.finalRange", "Line {{line}}: final % must be 0–100.", { line: l.position })); return; }
      if (f < l.contractedDiscountPct) { toast.error(t("admin.orders.error.belowContracted", "Line {{line}}: cannot go below contracted {{pct}}%.", { line: l.position, pct: l.contractedDiscountPct })); return; }
      if (f > l.contractedDiscountPct && !e.reason.trim()) { toast.error(t("admin.orders.error.reasonRequired", "Line {{line}}: reason required for extra discount.", { line: l.position })); return; }
      payload.push({ lineId: l.id, finalDiscountPct: f, reason: e.reason.trim() || null });
    }
    setBusy(true);
    try {
      const updated = await ordersApi.applyPricing(current.id, payload);
      setCurrent(updated);
      setEdits(Object.fromEntries(updated.lines.map((l) => [l.id, {
        final: String(l.finalDiscountPct ?? 0), reason: l.discountReason ?? "",
      }])));
      setSelected(new Set());
      toast.success(t("admin.orders.toast.discountsSaved", "Discounts saved."));
      await onChanged();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function refresh() {
    try {
      setCurrent(await ordersApi.get(order.id));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  async function transition(to: OrderStatus) {
    if (!canReview) return;
    // Mandatory rejection reason — mirror of the server
    // ORDER_REJECT_REASON_REQUIRED check; the notes field doubles as the reason.
    if (to === "REJECTED" && !salesNotes.trim()) {
      toast.error(t("orderEditor.reject.reasonRequired", "A reason is required to reject an order."));
      return;
    }
    setBusy(true);
    try {
      const updated = await ordersApi.transition(current.id, to, salesNotes || undefined);
      setCurrent(updated);
      toast.success(t("orderEditor.toast.orderMovedTo", "Order moved to {{status}}.", { status: t(`orders.status.${statusKey(to)}`, { defaultValue: to }) }));
      await onChanged();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function saveSalesNotes(e: FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setBusy(true);
    try {
      const updated = await ordersApi.update(current.id, { salesNotes });
      setCurrent(updated);
      toast.success(t("admin.orders.toast.notesSaved", "Notes saved."));
      await onChanged();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  // Whom the administrator lets order without approval approves their own order; nobody else's.
  const withoutApproval = hasPermission("orders:without_approval");
  const ownApproval = withoutApproval && current.createdBy === viewerId;
  const allowed = nextAllowedFor(current, viewerId, withoutApproval);
  // Approval asks for section:orders:full and a director role on top of the transition
  // key, so a reviewer who holds neither is offered everything but that one move.
  const canApprove = canFull && state.status === "authenticated"
    && state.user.roles.some((r) => ["ADMIN", "SUPERADMIN", "SALES_DIRECTOR"].includes(r));
  const reviewTargets = allowed.filter((to) => to !== "APPROVED" || canApprove || ownApproval);
  const approvalHeldByDirector = allowed.includes("APPROVED") && !canApprove && !ownApproval;
  const waitsForAnotherSigner = approvalWaitsForAnotherSigner(current, viewerId, withoutApproval);

  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: "var(--brand-ink)" }}>{current.orderNumber}</h2>
            <small style={{ color: "var(--ink-3)" }}>{current.companyName}</small>
          </div>
          <OrderStatusBadge status={current.status} />
        </header>
        {fromErp && <ErpReadOnlyNote text={t("orderEditor.fromErp")} />}

        <section>
          <h3 style={subTitle}>{t("orderEditor.lines.title", "Lines")}</h3>
          {canPrice && (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 8, padding: 10, background: "var(--surface-2)", borderRadius: 8 }}>
              <span style={{ fontSize: 12, color: "var(--ink-3)", alignSelf: "center" }}>{t("admin.orders.bulk.selected", { defaultValue: "{{count}} selected", count: selected.size })}</span>
              <label style={{ fontSize: 12, color: "var(--ink-3)", display: "flex", flexDirection: "column", gap: 2 }}>{t("admin.orders.col.finalPct", "Final %")}
                <input type="number" min={0} max={100} step="0.5" value={bulkPct} onChange={(ev) => setBulkPct(ev.target.value)} style={{ ...miniInput, width: 80 }} />
              </label>
              <label style={{ fontSize: 12, color: "var(--ink-3)", display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 160 }}>{t("admin.orders.col.reason", "Reason")}
                <input value={bulkReason} onChange={(ev) => setBulkReason(ev.target.value)} placeholder={t("admin.orders.bulk.reasonPlaceholder", "e.g. annual contract")} style={miniInput} />
              </label>
              <button type="button" disabled={busy} style={secondary} onClick={applyBulk}>{t("admin.orders.bulk.apply", "Apply to selected")}</button>
            </div>
          )}
          <table style={miniTbl}>
            <thead>
              <tr>
                {canPrice && <th style={th} />}
                <th style={th}>#</th>
                <th style={th}>{t("admin.orders.col.item", "Item")}</th>
                <th style={{ ...th, textAlign: "right" }}>{t("admin.orders.col.qty", "Qty")}</th>
                <th style={{ ...th, textAlign: "right" }}>{t("admin.orders.col.catalog", "Catalog")}</th>
                <th style={{ ...th, textAlign: "right" }}>{t("admin.orders.col.contractedPct", "Contr. %")}</th>
                <th style={{ ...th, textAlign: "right" }}>{t("admin.orders.col.finalPct", "Final %")}</th>
                {inReview && <th style={th}>{t("admin.orders.col.reason", "Reason")}</th>}
                <th style={{ ...th, textAlign: "right" }}>{t("admin.orders.col.net", "Net")}</th>
                <th style={{ ...th, textAlign: "right" }}>{t("orderEditor.table.lineTotal", "Line total")}</th>
              </tr>
            </thead>
            <tbody>
              {current.lines.map((l) => {
                const editable = canPrice && !!l.productId;
                const e = edits[l.id] ?? { final: "", reason: "" };
                const extra = Number(e.final) > l.contractedDiscountPct;
                return (
                  <tr key={l.id}>
                    {canPrice && (
                      <td style={td}>
                        {l.productId && (
                          <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggleSel(l.id)} aria-label={t("admin.orders.selectLineAria", "Select line {{line}}", { line: l.position })} />
                        )}
                      </td>
                    )}
                    <td style={td}>{l.position}</td>
                    <td style={td}>
                      {l.productSku ? <code>{l.productSku}</code> : null} {l.productName ?? l.freeTextDescription}
                      {showStockWarning && stockStatus(l.productSku) === "OUT" && (
                        <span style={stockOutTag}>{t("catalog.outOfStock", "Out of stock")}</span>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{l.quantity}&nbsp;{l.unit}</td>
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatUnitPrice(l.catalogUnitPrice, current.currency)}</td>
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{l.contractedDiscountPct}%</td>
                    <td style={{ ...td, textAlign: "right" }}>
                      {editable ? (
                        <input type="number" min={l.contractedDiscountPct} max={100} step="0.5" value={e.final}
                          onChange={(ev) => setEdit(l.id, { final: ev.target.value })}
                          style={{ ...miniInput, width: 64, textAlign: "right", fontWeight: extra ? 700 : 400 }} />
                      ) : (
                        <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: l.finalDiscountPct > l.contractedDiscountPct ? 700 : 400 }}>{l.finalDiscountPct}%</span>
                      )}
                    </td>
                    {inReview && (
                      <td style={td}>
                        {editable && extra ? (
                          <input value={e.reason} onChange={(ev) => setEdit(l.id, { reason: ev.target.value })}
                            placeholder={t("admin.orders.reasonPlaceholder", "reason required")}
                            style={{ ...miniInput, width: "100%", borderColor: e.reason.trim() ? "var(--border-strong)" : "var(--err)" }} />
                        ) : (
                          <span style={{ color: "var(--ink-3)" }}>{l.discountReason ?? ""}</span>
                        )}
                      </td>
                    )}
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatUnitPrice(canPrice ? liveNet(l) : l.netUnitPrice, current.currency)}</td>
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(canPrice ? liveTotal(l) : l.lineTotal, current.currency)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td style={{ ...td, textAlign: "right", fontWeight: 600 }} colSpan={7 + (canPrice ? 1 : 0) + (inReview ? 1 : 0)}>{t("admin.orders.orderTotal", "Order total")}</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{money(canPrice ? liveOrderTotal : current.orderTotal, current.currency)}</td>
              </tr>
            </tfoot>
          </table>
          {canPrice ? (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
              <button type="button" disabled={busy} style={{ ...secondary, background: "var(--brand-primary-600)", color: "var(--on-primary)", border: "1px solid var(--brand-primary-600)" }} onClick={savePricing}>{t("admin.orders.saveDiscounts", "Save discounts")}</button>
            </div>
          ) : inReview ? (
            <p style={noteLine}>
              {t("admin.orders.pricingReadOnly", "Popuste menja komercijalista koji vodi prelaze stanja porudžbine; ovde se samo čitaju.")}
            </p>
          ) : null}
        </section>

        {current.customerNotes && (
          <section>
            <h3 style={subTitle}>{t("orderEditor.details.customerNotes", "Customer notes")}</h3>
            <p style={{ margin: 0, fontSize: 13, color: "var(--ink-2)" }}>{current.customerNotes}</p>
          </section>
        )}

        {canWrite ? (
          <form onSubmit={saveSalesNotes} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <h3 style={subTitle}>{t("orderEditor.details.salesNotes", "Sales notes")}</h3>
            <textarea
              value={salesNotes}
              onChange={(e) => setSalesNotes(e.target.value)}
              rows={3}
              style={{ ...input, resize: "vertical", fontFamily: "inherit" }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="submit" disabled={busy} style={secondary}>{t("admin.orders.saveNotes", "Save notes")}</button>
            </div>
          </form>
        ) : (
          <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <h3 style={subTitle}>{t("orderEditor.details.salesNotes", "Sales notes")}</h3>
            <textarea
              value={salesNotes}
              readOnly
              rows={3}
              style={{ ...input, resize: "vertical", fontFamily: "inherit", background: "var(--surface-2)" }}
            />
            {!fromErp && (
              <p style={noteLine}>
                {t("admin.orders.notesReadOnly", "Napomenu prodaje upisuje ko sme da menja porudžbine; ovde se samo čita.")}
              </p>
            )}
          </section>
        )}

        {(reviewTargets.length > 0 || waitsForAnotherSigner || approvalHeldByDirector) && (
          <section>
            <h3 style={subTitle}>{t("admin.orders.lifecycle", "Lifecycle")}</h3>
            {canReview ? (
              <>
                <p style={{ color: "var(--ink-4)", fontSize: 12, margin: 0 }}>
                  {t("admin.orders.lifecycleHint", "Sales notes above will be saved with the transition.")}
                </p>
                {outOfStockSkuList.length > 0 && (
                  <div style={stockBanner}>
                    <Ic.alert />
                    <span>
                      <strong>{t("orders.stockWarning.title", "Possible stock shortage.")}</strong>{" "}
                      {t("orders.stockWarning.body", {
                        count: outOfStockSkuList.length,
                        defaultValue:
                          "{{count}} item(s) on this order may be out of stock — placeholder data until the ERP feed is connected. It isn’t blocked; review before approving.",
                      })}
                    </span>
                  </div>
                )}
                {waitsForAnotherSigner && (
                  <div style={signerBanner}>
                    <Ic.info width={15} height={15} style={{ flex: "none", marginTop: 1 }} />
                    <span>
                      <strong>{t("orders.approval.secondSigner.title", "This order waits for another signature.")}</strong>{" "}
                      {t("orders.approval.secondSigner.body", "An order is approved by someone other than the person who entered it: the sales director or an administrator. That is why the approval is not offered here.")}
                    </span>
                  </div>
                )}
                {approvalHeldByDirector && (
                  <p style={noteLine}>
                    {t("admin.orders.approvalDirectorOnly", "Odobrenje drži direktor prodaje ili administrator, pa ovde nije ponuđeno.")}
                  </p>
                )}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                  {reviewTargets.map((to) => (
                    <button type="button" key={to} onClick={() => transition(to)} disabled={busy} style={transitionBtn}>
                      → {t(`orders.status.${statusKey(to)}`, { defaultValue: to })}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <p style={noteLine}>
                {t("admin.orders.transitionsReadOnly", "Stanje porudžbine menja komercijalista sa pravom na prelaze; ovde se porudžbina samo pregleda.")}
              </p>
            )}
          </section>
        )}

        <button type="button" onClick={() => { void refresh(); }} style={{ ...secondary, alignSelf: "flex-end" }}>
          {t("admin.orders.refresh", "Refresh")}
        </button>
      </div>
    </div>
  );
}

function statusKey(status: OrderStatus): string {
  if (status === "IN_REVIEW") return "inReview";
  return status.toLowerCase();
}

const tbl: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13, background: "var(--surface)", borderRadius: 8, overflow: "hidden", boxShadow: "var(--shadow-1)" };
const miniTbl: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 12, marginTop: 8 };
const th: React.CSSProperties = { textAlign: "left", padding: "8px 10px", background: "var(--surface-2)", color: "var(--ink-3)", fontWeight: 600, borderBottom: "1px solid var(--border)" };
const td: React.CSSProperties = { padding: "8px 10px", borderBottom: "1px solid var(--border)" };
const input: React.CSSProperties = { padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border-strong)", fontSize: 14, width: "100%" };
const miniInput: React.CSSProperties = { padding: "4px 6px", borderRadius: 6, border: "1px solid var(--border-strong)", fontSize: 13 };
const stockBanner: React.CSSProperties = {
  display: "flex", alignItems: "flex-start", gap: 10, marginTop: 8,
  padding: "10px 12px", borderRadius: 8,
  background: "var(--tone-orange-bg)", border: "1px solid var(--tone-orange)", color: "var(--tone-orange)", fontSize: 12.5, lineHeight: 1.5,
};
const signerBanner: React.CSSProperties = {
  display: "flex", alignItems: "flex-start", gap: 10, marginTop: 8,
  padding: "10px 12px", borderRadius: 8,
  background: "var(--info-bg)", border: "1px solid var(--info)", color: "var(--info)", fontSize: 12.5, lineHeight: 1.5,
};
const stockOutTag: React.CSSProperties = {
  display: "inline-block", marginLeft: 6, padding: "0 6px", borderRadius: 999,
  fontSize: 10.5, fontWeight: 600,
  background: "var(--tone-orange-bg)", color: "var(--tone-orange)", border: "1px solid var(--tone-orange)", whiteSpace: "nowrap",
};
const noteLine: React.CSSProperties = { margin: "8px 0 0", fontSize: 12, color: "var(--ink-3)", lineHeight: 1.5 };
const linkBtn: React.CSSProperties = { background: "transparent", border: "none", color: "var(--brand-ink)", cursor: "pointer", fontSize: 13 };
const secondary: React.CSSProperties = { padding: "8px 14px", background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const transitionBtn: React.CSSProperties = { padding: "6px 12px", background: "var(--tone-indigo-bg)", color: "var(--tone-indigo)", border: "1px solid var(--tone-indigo)", borderRadius: 6, fontSize: 12, cursor: "pointer" };
const subTitle: React.CSSProperties = { margin: "16px 0 4px", fontSize: 12, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.5 };
const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 };
const dialog: React.CSSProperties = { width: "100%", maxWidth: 760, background: "var(--surface)", padding: 24, borderRadius: 12, boxShadow: "var(--shadow-2)", display: "flex", flexDirection: "column", gap: 14, maxHeight: "90vh", overflowY: "auto" };
